import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../modules/auth";
import { TablaUsuarios } from "../../components/TablaUsuarios/TablaUsuarios";
import { Modal } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { ModalUsuario } from "../../components/ModalUsuario/ModalUsuario";
import { ModalNuevoUsuario } from "../../components/ModalUsuario/ModalNuevoUsuario";
import { KTSVG } from "../../../_metronic/helpers";

/* ================= VALIDACIONES ================= */

const initialValues = {
  firstname: "",
  lastname: "",
  email: "",
  phone: "",
  company: "",
  employees: "",
  city: "",
  state: "",
  password: "",
  changepassword: "",
  plan: "",
  status: "",
  paymentMethod: "",
  paymentDate: "",
};

const registrationSchema = (isEditing) =>
  Yup.object().shape({
    firstname: Yup.string().required("Nombre es requerido"),
    lastname: Yup.string().required("Apellido es requerido"),
    email: Yup.string().email("Correo inválido").required("Correo es requerido"),
    phone: Yup.string().required("Teléfono es requerido"),

    company: Yup.string().required("Empresa requerida"),
    employees: Yup.number()
      .typeError("Debe ser un número")
      .positive("Debe ser mayor a 0")
      .required("Número de empleados requerido"),
    city: Yup.string().required("Ciudad requerida"),
    state: Yup.string().required("Estado requerido"),

    password: isEditing ? Yup.string() : Yup.string().required("Contraseña requerida"),
    changepassword: Yup.string().when("password", (password, schema) => {
      if (!password) return isEditing ? schema.notRequired() : schema.required();
      return schema.oneOf([Yup.ref("password")], "No coincide").required();
    }),
    plan: Yup.string().required("Plan requerido"),
    status: Yup.string().required("Estatus requerido"),
    paymentMethod: Yup.string().required("Método requerido"),
    paymentDate: Yup.date().required("Fecha requerida"),
  });

export function Usuarios() {
  const API_URL = process.env.REACT_APP_API_URL;
  const { currentUser } = useAuth();

  const [usuarios, setUsuarios] = useState([]);
  const [loadingPage, setLoadingPage] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [usuarioDetalle, setUsuarioDetalle] = useState(null);

  const [showNewUser, setShowNewUser] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [loadingNewUser, setLoadingNewUser] = useState(false);

  /* ========== FETCH ========== */
  const misUsuarios = async () => {
    setLoadingPage(true);
    try {
      const res = await axios.get(`${API_URL}/app/usuarios/${currentUser.id}`);
      setUsuarios(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    misUsuarios();
  }, []);

  /* ========== FORM ========== */
  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: registrationSchema(isEditing),
    onSubmit: async (values) => {
      setLoadingNewUser(true);
      try {
        if (isEditing && editingUserId) {
          await axios.post(`${API_URL}/app/updateUserManual`, {
            id_user: editingUserId,
            first_name: values.firstname,
            last_name: values.lastname,
            email: values.email,
            password: values.password || undefined,
            phone: values.phone,

            company: values.company,
            employees: values.employees,
            city: values.city,
            state: values.state,

            plan: values.plan,
            status: values.status,
            payment_method: values.paymentMethod,
            payment_date: values.paymentDate,
          });
          Swal.fire("Usuario actualizado", "", "success");
        } else {
          await axios.post(`${API_URL}/app/newUser`, {
            first_name: values.firstname,
            last_name: values.lastname,
            email: values.email,
            password: values.password,
            phone: values.phone,

            company: values.company,
            employees: values.employees,
            city: values.city,
            state: values.state,

            plan: values.plan,
            payment_status: values.status,
            payment_method: values.paymentMethod,
            payment_date: values.paymentDate,
          });
          Swal.fire("Usuario creado", "", "success");
        }
        setShowNewUser(false);
        misUsuarios();
      } catch (e) {
        Swal.fire("Error", "No se pudo guardar", "error");
      } finally {
        setLoadingNewUser(false);
      }
    },
  });

  /* ========== ACCIONES ========== */

  const handleNuevoUsuario = () => {
    setIsEditing(false);
    setEditingUserId(null);
    formik.resetForm();
    setShowNewUser(true);
  };

  const editUser = (id) => {
    const user = usuarios.find((u) => u.id === id);
    if (!user) return;

    setIsEditing(true);
    setEditingUserId(id);

    formik.setValues({
      firstname: user.first_name || "",
      lastname: user.last_name || "",
      email: user.email || "",
      phone: user.phone || "",

      company: user.company || "",
      employees: user.employees || "",
      city: user.city || "",
      state: user.state || "",

      password: "",
      changepassword: "",
      plan: user.plan || "",
      status: user.subscription || "",
      paymentMethod: user.payment_method || "",
      paymentDate: user.payment_date?.split("T")[0] || "",
    });

    setShowNewUser(true);
  };

  const deleteUser = (id) => {
    Swal.fire({
      title: "¿Desea eliminar el usuario?",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      confirmButtonColor: "#C6313A",
    }).then(async (res) => {
      if (res.isConfirmed) {
        await axios.post(`${API_URL}/app/deleteUser`, { idUser: id });
        misUsuarios();
        Swal.fire("Usuario eliminado", "", "success");
      }
    });
  };

  const showUser = async (id) => {
    const res = await axios.get(`${API_URL}/app/myProfile/${id}`);
    setUsuarioDetalle(res.data);
    setShowModal(true);
  };

  const filteredUsuarios = usuarios.filter((u) =>
    Object.values(u).join(" ").toLowerCase().includes(searchValue.toLowerCase())
  );

  /* ========== UI ========== */

  return (
    <>
      {/* MODAL VER USUARIO */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Detalle del Usuario</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {usuarioDetalle && <ModalUsuario usuario={usuarioDetalle} />}
        </Modal.Body>
      </Modal>

      {/* MODAL NUEVO / EDITAR */}
      <ModalNuevoUsuario
        formik={formik}
        show={showNewUser}
        onClose={() => setShowNewUser(false)}
        isEditing={isEditing}
        loading={loadingNewUser}
      />

      {/* HEADER */}
      <div className="row gy-5 g-xl-8">
        <div className="col-lg-8">
          <h2>Bienvenido</h2>
          <p>{new Date().toLocaleString()}</p>
        </div>
      </div>

      {/* CARD */}
      <div className="row gy-5 g-xl-8 card card-custom shadow mt-4 pb-10">
        <div className="row p-6 align-items-center">
          <div className="col-md-4">
           
          </div>

          <div className="col-md-8 d-flex justify-content-end">
            <input
              type="text"
              className="form-control form-control-sm w-25 me-3"
              placeholder="Buscar..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />

            <button id="tabla-excel-button" className="btn btn-success btn-sm me-3">
              <KTSVG path="/media/icons/duotune/files/fil002.svg" className="svg-icon-2 me-1" />
              Exportar Excel
            </button>

            <button
              style={{ backgroundColor: "#FE735B", color: "#fff" }}
              onClick={handleNuevoUsuario}  className="btn  btn-sm">
              <KTSVG path="/media/icons/duotune/communication/com013.svg" className="svg-icon-2 me-1 text-white" />
              Nuevo Usuario
            </button>
          </div>
        </div>

        <div className="row px-6 pb-6">
          <TablaUsuarios
            usuarios={filteredUsuarios}
            showUser={showUser}
            editUser={editUser}
            deleteUser={deleteUser}
            excelButtonId="tabla-excel-button"
          />
        </div>
      </div>
    </>
  );
}